/**
 * Context Command — Token Budget Visualization
 * Shows how the 256K context window is distributed per IDE
 * Inspired by Claude Code /context stacked bar style
 */
import { Command } from 'commander';
import chalk from 'chalk';
import { ContextScannerService } from '../services/context-scanner.service.js';
import { IDE_CONFIGS } from '../constants/ide-configs.js';
import type { IDE, IDEContext } from '../types/context.types.js';
import { getCliName } from '../utils/cli-name.js';

// ─── Constants ──────────────────────────────────────────
const MAX_TOKENS = 256_000;
const SYSTEM_PROMPT_TOKENS = 10_000;
const COMPACT_BUFFER_TOKENS = 56_000; // 200K → 256K reserved for compress/summary
const BAR_WIDTH = 50;
const CHARS_PER_TOKEN = 4; // heuristic: ~4 chars ≈ 1 token

// ─── Helpers ────────────────────────────────────────────

function estimateTokens(bytes: number): number {
    return Math.ceil(bytes / CHARS_PER_TOKEN);
}

function formatTokens(n: number): string {
    if (n >= 1000) {
        const k = n / 1000;
        return k % 1 === 0 ? `${k}k` : `${k.toFixed(1)}k`;
    }
    return String(n);
}

function formatNumber(n: number): string {
    return n.toLocaleString('en-US');
}

function pct(value: number, total: number): string {
    return ((value / total) * 100).toFixed(1) + '%';
}

// ─── Stacked Bar ────────────────────────────────────────

interface BarSegment {
    tokens: number;
    char: string;
    color: (s: string) => string;
}

function renderStackedBar(segments: BarSegment[], total: number, width: number): string {
    let bar = '';
    let usedCols = 0;

    for (let i = 0; i < segments.length; i++) {
        const seg = segments[i]!;
        const isLast = i === segments.length - 1;
        const cols = isLast
            ? width - usedCols
            : Math.round((seg.tokens / total) * width);
        if (cols > 0) {
            bar += seg.color(seg.char.repeat(cols));
            usedCols += cols;
        }
    }

    return bar;
}

// ─── Visualization ──────────────────────────────────────

function renderIDEContext(ideContext: IDEContext): void {
    const { config, items } = ideContext;

    // Calculate rules tokens
    const ruleItems = items.filter(item => item.type === 'rules');
    const rulesTotalBytes = ruleItems.reduce((sum, item) => sum + item.fileSize, 0);
    const rulesTokens = estimateTokens(rulesTotalBytes);

    // Calculate totals
    const usedTokens = SYSTEM_PROMPT_TOKENS + rulesTokens;
    const userPromptTokens = Math.max(0, MAX_TOKENS - SYSTEM_PROMPT_TOKENS - rulesTokens - COMPACT_BUFFER_TOKENS);

    // ── Header ──
    console.log(`${chalk.dim('└')}  ${chalk.bold('Context Usage')}`);

    // ── Stacked bar ──
    const segments: BarSegment[] = [
        { tokens: SYSTEM_PROMPT_TOKENS, char: '▓', color: chalk.yellow },
        { tokens: rulesTokens, char: '▓', color: chalk.red },
        { tokens: userPromptTokens, char: '░', color: chalk.dim },
        { tokens: COMPACT_BUFFER_TOKENS, char: '▒', color: chalk.magenta },
    ];

    const barLine = renderStackedBar(segments, MAX_TOKENS, BAR_WIDTH);
    const summary = `${config.icon} ${config.name} · ${formatTokens(usedTokens)}/${formatTokens(MAX_TOKENS)} tokens (${pct(usedTokens, MAX_TOKENS)})`;

    // Bar row 1 — stacked bar + summary
    console.log(`     ${barLine}    ${chalk.bold(summary)}`);

    // ── Legend ──
    const legendItems = [
        { icon: '🟡', label: 'System prompt', tokens: SYSTEM_PROMPT_TOKENS },
        { icon: '🔴', label: 'IDE Rules', tokens: rulesTokens },
        { icon: '⬜', label: 'Free space', tokens: userPromptTokens },
        { icon: '🟣', label: 'Autocompact buffer', tokens: COMPACT_BUFFER_TOKENS },
    ];

    const pad = ' '.repeat(BAR_WIDTH + 5);
    for (const item of legendItems) {
        console.log(`${pad}    ${item.icon} ${item.label}: ${formatTokens(item.tokens)} tokens (${pct(item.tokens, MAX_TOKENS)})`);
    }

    // ── Rules breakdown ──
    if (ruleItems.length > 0) {
        console.log('');
        const rulesPath = config.id === 'opencode' ? 'AGENTS.md' : `${config.basePath}/rules`;
        console.log(`   ${chalk.bold('Rules')} ${chalk.dim(`· ${rulesPath}`)}`);
        const sorted = [...ruleItems].sort((a, b) => b.fileSize - a.fileSize);
        for (const rule of sorted) {
            const tokens = estimateTokens(rule.fileSize);
            const name = typeof rule.name === 'string' ? rule.name : rule.relativePath;
            const badge = rule.alwaysApply ? chalk.dim(' [always]') : '';
            console.log(`   ${chalk.dim('└')} ${name}${badge}: ${formatNumber(tokens)} tokens`);
        }
    }

    // ── Workflows listing ──
    const workflowItems = items.filter(item => item.type === 'workflows');
    if (workflowItems.length > 0) {
        console.log('');
        const wfPath = config.contentPaths.workflows || 'workflows';
        const wfDisplayPath = config.basePath === '.' ? wfPath : `${config.basePath}/${wfPath}`;
        console.log(`   ${chalk.bold('Workflows')} ${chalk.dim(`· ${wfDisplayPath}`)}`);
        const sorted = [...workflowItems].sort((a, b) => b.fileSize - a.fileSize);
        for (const wf of sorted) {
            const tokens = estimateTokens(wf.fileSize);
            const name = typeof wf.name === 'string' ? wf.name : wf.relativePath;
            console.log(`   ${chalk.dim('└')} ${name}: ${formatNumber(tokens)} tokens`);
        }
    }

    console.log('');
}

// ─── Command ────────────────────────────────────────────

export function createContextCommand(): Command {
    const cmd = new Command('context')
        .description('Visualize IDE context window token budget')
        .argument('[ide]', 'Show specific IDE only (cursor, windsurf, antigravity, claude, opencode)')
        .action(async (ideArg?: string) => {
            const name = getCliName();

            // Validate IDE argument
            const allIDEs: IDE[] = ['cursor', 'windsurf', 'antigravity', 'claude', 'opencode'];
            if (ideArg) {
                if (!allIDEs.includes(ideArg as IDE)) {
                    console.error(chalk.red(`❌ Invalid IDE: ${ideArg}`));
                    console.error(chalk.dim(`   Valid IDEs: ${allIDEs.join(', ')}`));
                    console.error(chalk.dim(`   Usage: ${name} context [ide]`));
                    process.exit(1);
                }
            }

            const scanner = new ContextScannerService();

            // Detect only IDEs that are actually installed (directory exists)
            const detectedIDEs = await scanner.detectIDEs();
            const installedIDEs = detectedIDEs.filter(ide => allIDEs.includes(ide));
            const targetIDEs = ideArg ? [ideArg as IDE] : installedIDEs;

            // Header
            console.log('');
            console.log(` ${chalk.bgGray.white.bold(' /context ')}`);

            let found = false;

            for (const ide of targetIDEs) {
                try {
                    const ideContext = await scanner.scanIDE(ide);
                    const config = IDE_CONFIGS[ide];
                    if (config) {
                        renderIDEContext(ideContext);
                        found = true;
                    }
                } catch {
                    // IDE not present — skip
                }
            }

            if (!found) {
                console.log(chalk.yellow(' ⚠️  No IDEs detected in this project.'));
                console.log(chalk.dim(`    Run "${name} apply" to install context for your IDE.\n`));
            }
        });

    return cmd;
}
